import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Alert, Card, CardContent, Select } from '@guildeducationinc/recess';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav';
import { ACCREDITATIONS, SAVE_ACCREDITATION } from '@src/graphql/queries/accreditation';
import { useAccreditations } from '@src/hooks/useAccreditations';
import { useAuth } from '@src/hooks/useAuth';
import { useClients } from '@src/hooks/useClients';
import { prepareForSave } from '@src/utilities/gqlHelper';
import { GlobalPermission } from '@src/utilities/permissions';
import { compact, omit } from 'lodash';
import { DataTable, Form, SearchFilterBar, TrackableButton, useLocale } from 'pe-core-frontend';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
export const AccreditationsList = () => {
    const { t } = useLocale();
    const { hasPermission } = useAuth();
    const { accreditations, loading, error } = useAccreditations();
    const { clients } = useClients({ initialPageSize: 100 });
    const [selectedAccreditation, setSelectedAccreditation] = useState(null);
    const [saveAccreditation] = useMutation(SAVE_ACCREDITATION, {
        refetchQueries: [ACCREDITATIONS],
    });
    const [saving, setSaving] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [searchText, setSearchText] = useState('');
    // Filter accreditations by search text
    const filteredAccreditations = useMemo(() => {
        if (!searchText)
            return accreditations;
        const lowerSearch = searchText.toLowerCase();
        return accreditations.filter(acc => acc.name?.toLowerCase().includes(lowerSearch) ||
            acc.description?.toLowerCase().includes(lowerSearch) ||
            acc.clients?.some(client => client.name.toLowerCase().includes(lowerSearch)));
    }, [accreditations, searchText]);
    const clientOptions = useMemo(() => {
        return clients.map(client => ({
            value: client.id.toString(),
            label: client.name,
        }));
    }, [clients]);
    const Fields = useMemo(() => [
        {
            name: 'name',
            label: t('Name'),
            type: 'text',
        },
        {
            name: 'description',
            label: t('Description'),
            type: 'textarea',
            plainText: true,
        },
    ], [t]);
    const handleValidate = (acc) => {
        const errors = {};
        if (!acc.name) {
            errors.name = t('{field} is required', { values: { field: t('Name') } });
        }
        return errors;
    };
    const handleSave = async (acc) => {
        try {
            setSaving(true);
            // Transform clients to clientIds for the mutation
            const input = {
                ...omit(prepareForSave(acc), 'schoolsCount', 'clients'),
                clientIds: acc.clients?.map(c => c.id) || [],
            };
            await saveAccreditation({
                variables: { input },
            });
            setSelectedAccreditation(null);
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            throw new Error(t('Error saving school grouping'));
        }
        finally {
            setSaving(false);
        }
    };
    const handleClientsChange = (setDraft) => (selectedClientIds) => {
        // Transform selected client IDs to ClientInfo objects
        const selectedClients = clients
            .filter(c => selectedClientIds.includes(c.id))
            .map(c => ({ id: c.id, name: c.name }));
        setDraft(prev => ({
            ...prev,
            clients: selectedClients,
        }));
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Schools'), navLinks: [
                    {
                        title: t('Countries'),
                        path: '/admin/schools/countries',
                    },
                    {
                        title: t('Groups'),
                        path: '/admin/schools/accreditations',
                        selected: true,
                    },
                ], searchFilterActions: _jsx(SearchFilterBar, { enableSearch: true, searchText: searchText, searchPlaceholder: t('Search by Name, Description, or Client'), onSearchChange: searchText => {
                        setSearchText(searchText);
                        setPage(0);
                    } }), actions: hasPermission(GlobalPermission.CREATE_ACCREDITATION) && (_jsx(TrackableButton, { btnType: 'mui', enText: 'Add', tag: 'admin_header', variant: 'contained', disabled: saving, onClick: () => {
                        setSelectedAccreditation({
                            id: 0,
                            name: '',
                            description: '',
                            clients: [],
                        });
                    }, children: t('Add') })) }), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), !loading && filteredAccreditations.length === 0 && (_jsx(Alert, { display: true, hideClose: true, type: 'info', className: 'mt-4 mb-4', children: searchText ? t('No school groupings found matching search') : t('No school groupings found') })), _jsx(DataTable, { disableColumnMenu: true, disableRowSelectionOnClick: true, loading: loading, rows: filteredAccreditations, pageSizeOptions: [10, 25, 50, 100], paginationMode: 'client', paginationModel: { page, pageSize }, onPaginationModelChange: pagination => {
                                    setPage(pagination.page);
                                    setPageSize(pagination.pageSize);
                                }, columns: compact([
                                    {
                                        field: 'name',
                                        headerName: t('Name'),
                                        flex: 0.2,
                                        renderCell: ({ value, row }) => (_jsx(Link, { to: `/admin/schools/accreditation/${row.id}`, children: value })),
                                    },
                                    {
                                        field: 'description',
                                        headerName: t('Description'),
                                        flex: 0.6,
                                    },
                                    {
                                        field: 'clients',
                                        headerName: t('Clients'),
                                        flex: 0.2,
                                        sortable: false,
                                        renderCell: ({ row }) => row.clients && row.clients.length > 0
                                            ? row.clients.map(c => c.name).join(', ')
                                            : '',
                                    },
                                    {
                                        field: 'schoolsCount',
                                        headerName: t('Schools Count'),
                                        flex: 0.2,
                                    },
                                    hasPermission(GlobalPermission.UPDATE_ACCREDITATION) && {
                                        field: 'action',
                                        headerName: t('Actions'),
                                        renderCell: ({ row }) => (_jsx("div", { children: _jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `accreditation_${row.id}`, label: t('Edit'), appearance: 'minimal', icon: _jsx(EditIcon, { size: 20 }), onClick: () => setSelectedAccreditation(row), className: 'margin--right_8' }) })),
                                        width: 100,
                                        sortable: false,
                                    },
                                ]) }), selectedAccreditation && (_jsx(Form, { title: selectedAccreditation.id ? t('Edit Group') : t('Add Group'), modal: true, obj: selectedAccreditation, fields: Fields, onValidate: handleValidate, onSave: handleSave, onCancel: () => setSelectedAccreditation(null), children: ({ draft, setDraft }) => (_jsx(Select, { easyPortal: true, label: t('Clients'), options: clientOptions, value: (draft.clients || []).map(c => c.id.toString()), onChange: (e) => {
                                        const values = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
                                        const selectedValues = values
                                            .filter((v) => v !== null)
                                            .map(v => parseInt(v));
                                        handleClientsChange(setDraft)(selectedValues);
                                    }, multiple: true })) }))] }) }) })] }));
};
